//src/pages/SignupLoadingPage.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import { useLoading } from '../contexts/LoadingContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization2 } from '../contexts/OrganizationContext2';
import { db, auth, functions } from '../firebase';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import { useFormContext } from '../contexts/FormContext';
import { duplicateForm } from '../utils/duplicateForm';
import { TEMPLATE_REGISTRY } from '../utils/templateRegistry';
import {
  ONBOARDING_EVENTS,
  ONBOARDING_EVENT_SOURCES,
  SIGNUP_INTENTS,
  SIGNUP_TYPES,
} from '../utils/onboardingLifecycle';

const SignupLoadingPage = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { setAuthState, organizationId: orgIdFromAuth } = useAuth();
  const { createOrganization } = useOrganization2();
  const { initializeNewFormLocally } = useFormContext();

  const [error, setError] = useState('');

  // Ref to ensure doHeavyWork only runs once in dev mode (React Strict Mode)
  const hasRunRef = useRef(false);

  //  Enterprise UX: progress + status text (step-based, not time-based)
  const [progress, setProgress] = useState(8);
  const [statusText, setStatusText] = useState('Starting setup…');

  //  Enterprise UX: gentle “don’t close” reminder + long-running reassurance
  const [showDontClose, setShowDontClose] = useState(true);
  const [longWaitMsg, setLongWaitMsg] = useState('');

  //  Enterprise UX: step checklist (gives feedback even if a step takes time)
  // Steps are purely UI (does not change backend logic)
  const [stepState, setStepState] = useState({
    workspace: 'pending',   // pending | active | done
    profile: 'pending',
    sync: 'pending',
    finalize: 'pending',
  });

  //  Enterprise UX: allow retry after a timeout (discourages early clicking)
  const [showRetry, setShowRetry] = useState(false);

  const logoSrc = process.env.PUBLIC_URL + '/utmlogo.svg';

  const setStep = (pct, text) => {
    setProgress((prev) => Math.max(prev, pct));
    setStatusText(text);
  };

  const setChecklist = (patch) => {
    setStepState((prev) => ({ ...prev, ...patch }));
  };

  // ─────────────────────────────────────────────────────────────
  // Smooth ticker: gently increments progress up to a “cap”
  // while a step is running, so it never feels stuck.
  // ─────────────────────────────────────────────────────────────
  const tickerRef = useRef(null);

  const stopTicker = () => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  };

  const startTicker = (capPct) => {
    stopTicker();
    tickerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= capPct) return prev;

        // Small increments that slow down near the cap
        const remaining = capPct - prev;
        const bump =
          remaining > 10 ? 1.2 :
          remaining > 5 ? 0.8 :
          0.4;

        const next = prev + bump;
        return next > capPct ? capPct : next;
      });
    }, 350);
  };

  // Cleanup ticker on unmount
  useEffect(() => {
    return () => stopTicker();
  }, []);

  //  Warn on tab close / refresh while setup is in progress (best-effort)
  useEffect(() => {
    const beforeUnload = (e) => {
      // Only block/confirm while we’re doing work and not in an error state
      if (!error && progress < 100) {
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [error, progress]);

  // Better “stuck” messaging threshold
  useEffect(() => {
    if (error) return;

    const t1 = setTimeout(() => {
      setLongWaitMsg(
        'Still working… creating your workspace can take a bit.'
      );
    }, 8000);

    const t2 = setTimeout(() => {
      setLongWaitMsg(
        'This is taking longer than usual. Please keep this tab open.'
      );
    }, 20000);

    //  Show retry option only after a longer threshold
    const t3 = setTimeout(() => {
      setShowRetry(true);
    }, 45000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [error]);

  // We'll replicate your original Steps 2–6 here.
  useEffect(() => {
    // Retrieve needed data from sessionStorage
    const uid = sessionStorage.getItem('signupUserUid');
    const email = sessionStorage.getItem('signupUserEmail');
    const orgName = sessionStorage.getItem('signupOrgName');

    if (!uid || !email) {
      console.warn(
        '[SignupLoadingPage] Missing user data. ' +
        'Redirecting to /signup.'
      );
      navigate('/signup');
      return;
    }

    //  One-Time Guard: If we've already run, skip re-running heavy-lifting
    if (hasRunRef.current) {
      console.log(
        '[SignupLoadingPage] Heavy-lifting already done. ' +
        'Skipping repeated mount.'
      );
      return;
    }
    hasRunRef.current = true;

    // We'll run the heavy-lifting in an async function.
    const doHeavyWork = async () => {
      setLoading(true, 'SignupLoading');

      const normalizeDateValue = (value) => {
        if (!value) return null;

        try {
          if (typeof value === 'string') {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime())
              ? null
              : parsed.toISOString();
          }

          if (value instanceof Date) {
            return Number.isNaN(value.getTime())
              ? null
              : value.toISOString();
          }

          if (typeof value.toDate === 'function') {
            const parsed = value.toDate();
            return Number.isNaN(parsed.getTime())
              ? null
              : parsed.toISOString();
          }

          if (value.seconds != null) {
            return new Date(value.seconds * 1000).toISOString();
          }
        } catch (e) {
          console.warn(
            '[SignupLoadingPage] Failed to normalize date value:',
            e?.message || e
          );
        }

        return null;
      };

      const recordSignupOnboardingEvent = async ({
        eventName,
        eventId,
        eventProperties = {},
        metadata = {},
      }) => {
        try {
          const recordOnboardingEvent = httpsCallable(
            functions,
            'recordOnboardingEvent'
          );

          const response = await recordOnboardingEvent({
            eventName,
            eventId,
            insertId: eventId,
            eventSource: ONBOARDING_EVENT_SOURCES.SIGNUP_LOADING_PAGE,
            eventProperties,
            metadata: {
              ...metadata,
              source: 'SignupLoadingPage',
            },
          });

          console.log('[SignupLoadingPage] Onboarding event recorded:', {
            eventName,
            eventId,
            result: response?.data || null,
          });

          return response?.data || null;
        } catch (eventErr) {
          console.warn(
            '[SignupLoadingPage] Onboarding event failed (non-fatal):',
            {
              eventName,
              eventId,
              error: eventErr?.message || eventErr,
            }
          );

          return null;
        }
      };

      try {
        console.log('--- Begin heavy-lifting in SignupLoadingPage ---');
        console.log('Email:', email);
        console.log('Organization Name:', orgName);

        // Step 2: Create organization
        setChecklist({
          workspace: 'active',
          profile: 'pending',
          sync: 'pending',
          finalize: 'pending',
        });
        setStep(12, 'Creating your workspace…');
        startTicker(28); // tick 12 → 28 while waiting

        let organizationId = '';
        if (orgName) {
          console.log('Creating organization...');
          organizationId = await createOrganization(orgName, uid, email);
          console.log('Organization created with ID:', organizationId);
        } else {
          console.warn(
            'Organization name is missing. Skipping organization creation.'
          );
        }

        stopTicker();
        setChecklist({ workspace: 'done', profile: 'active' });
        setStep(40, 'Saving your profile…');
        startTicker(55); // tick 40 → 55 while waiting

        // Step 3: Save user profile to Firestore if organization was created
        if (organizationId) {
          console.log('Saving user profile to Firestore...');
          const userDocRef = doc(db, 'accounts', uid);
          await setDoc(
            userDocRef,
            {
              email,
              organizationId,
              role: 'owner', // Assign 'owner' role for organization creator
            },
            { merge: true }
          );
          console.log(`User profile saved to Firestore for UID: ${uid}`);
        } else {
          console.warn(
            'Organization ID missing. Skipping user profile save.'
          );
        }

        stopTicker();
        setChecklist({ profile: 'done', sync: 'active' });
        setStep(60, 'Confirming setup…');
        startTicker(72); // tick 60 → 72 while polling

        // Step 4: Wait for Firestore profile sync
        console.log('Waiting for Firestore profile to sync...');
        let profileExists = false;
        const maxRetries = 5;
        let attempts = 0;

        while (!profileExists && attempts < maxRetries) {
          const userDocRef = doc(db, 'accounts', uid);
          const userSnap = await getDoc(userDocRef);

          if (userSnap.exists()) {
            console.log('Firestore profile found:', userSnap.data());
            profileExists = true;
          } else {
            attempts++;
            console.warn(
              `Firestore profile not yet available. ` +
              `Retrying (${attempts}/${maxRetries})...`
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        if (!profileExists) {
          throw new Error(
            'Failed to confirm Firestore profile sync after retries.'
          );
        }

        stopTicker();
        setChecklist({ sync: 'done', finalize: 'active' });
        setStep(75, 'Finalizing account preferences…');
        startTicker(84); // tick 75 → 84 while optional call runs

        // Step 4.1: Record TOS/Privacy acceptance (optional)
        try {
          // Retrieve the single acceptance flag from sessionStorage
          const tosAccepted =
            sessionStorage.getItem('signupTosAccepted') === 'true';

          // If user checked the single combined checkbox
          if (tosAccepted) {
            console.log(
              '[SignupLoadingPage] Recording TOS/Privacy acceptance ' +
              'via recordTosAcceptance...'
            );
            const recordAcceptance = httpsCallable(
              functions,
              'recordTosAcceptance'
            );
            // Pass a version or any data you need
            await recordAcceptance({ version: '1.0' });
            console.log(
              '[SignupLoadingPage] TOS/Privacy acceptance ' +
              'recorded successfully.'
            );
          } else {
            console.log(
              '[SignupLoadingPage] TOS/Privacy not accepted or missing ' +
              'flags. Skipping acceptance call.'
            );
          }
        } catch (acceptErr) {
          console.error(
            '[SignupLoadingPage] Error calling recordTosAcceptance:',
            acceptErr.message || acceptErr
          );
          // If you want to block or sign the user out, do so here
        }

        stopTicker();
        setStep(88, 'Signing you in…');
        startTicker(96); // tick 88 → 96 while auth hydrates

        // Step 5: Update user authentication state
        console.log('Updating user authentication state...');
        try {
          const currentFirebaseUser = auth.currentUser;
          if (!currentFirebaseUser) {
            throw new Error(
              'No current user found in Firebase Auth. ' +
              'Potentially signed out.'
            );
          }

          // 🔹 Force a fresh profile fetch now that Firestore write is done
          await setAuthState(currentFirebaseUser, { forceRefresh: true });

          console.log('User authentication state updated successfully.');
        } catch (authError) {
          console.error(
            'Error in setAuthState:',
            authError.message || authError
          );
          throw new Error(
            'Failed to update user authentication state. ' +
            'Please try again.'
          );
        }

        stopTicker();
        setChecklist({ finalize: 'done' });
        setStep(100, 'All set! Redirecting…');
        setShowDontClose(false);

        // Step 6: Intent-based redirect (template duplication bootstrap)
        try {
          const intent = sessionStorage.getItem('signupIntent');
          const templateKey = sessionStorage.getItem('signupTemplateKey');
          const source = sessionStorage.getItem('signupSource');

          // NEW: resolve organizationId from Firestore as the most reliable source
          // after the profile is confirmed (Step 4).
          let orgIdFromAccount = null;
          try {
            const userDocRef = doc(db, 'accounts', uid);
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
              orgIdFromAccount = userSnap.data()?.organizationId || null;
            }
          } catch (e) {
            console.warn(
              '[SignupLoadingPage] Failed to resolve orgId from accounts doc (non-fatal):',
              e?.message || e
            );
          }

          // NEW: final org id selection for bootstrap
          // Priority:
          //  1) accounts/{uid}.organizationId (confirmed)
          //  2) organizationId returned from createOrganization()
          //  3) orgIdFromAuth (may lag behind setAuthState)
          const organizationIdForBootstrap =
            orgIdFromAccount || organizationId || orgIdFromAuth || null;

          let organizationForOnboarding = null;

          if (organizationIdForBootstrap) {
            try {
              const orgDocRef = doc(
                db,
                'organizations',
                organizationIdForBootstrap
              );
              const orgSnap = await getDoc(orgDocRef);

              if (orgSnap.exists()) {
                organizationForOnboarding = {
                  id: orgSnap.id,
                  ...orgSnap.data(),
                };
              }
            } catch (e) {
              console.warn(
                '[SignupLoadingPage] Failed to resolve organization ' +
                'billing state for onboarding (non-fatal):',
                e?.message || e
              );
            }
          }

          console.log('[SignupLoadingPage] Bootstrap intent snapshot:', {
            intent,
            templateKey,
            source,
            orgIdFromAuth,
            orgIdFromAccount,
            organizationId,
            organizationIdForBootstrap,
            trialStartsAt:
              normalizeDateValue(organizationForOnboarding?.trialStartsAt),
            trialEndsAt:
              normalizeDateValue(organizationForOnboarding?.trialEndsAt),
            subscriptionStatus:
              organizationForOnboarding?.subscriptionStatus || null,
            planKey:
              organizationForOnboarding?.planKey ||
              organizationForOnboarding?.planName ||
              null,
          });

          const signupIntentForOnboarding =
            intent === SIGNUP_INTENTS.DUPLICATE_TEMPLATE && templateKey
              ? SIGNUP_INTENTS.DUPLICATE_TEMPLATE
              : SIGNUP_INTENTS.GENERAL_SIGNUP;

          await recordSignupOnboardingEvent({
            eventName: ONBOARDING_EVENTS.TRIAL_STARTED,
            eventId: `signup_${uid}_trial_started`,
            eventProperties: {
              signupType: SIGNUP_TYPES.SELF_SERVE_TRIAL,
              signupIntent: signupIntentForOnboarding,
              signupSource: source || 'unknown',
              ...(templateKey ? { templateKey } : {}),
              subscriptionStatus:
                organizationForOnboarding?.subscriptionStatus ||
                'trialing',
              trialStartedAt:
                normalizeDateValue(
                  organizationForOnboarding?.trialStartsAt
                ),
              trialEndsAt:
                normalizeDateValue(
                  organizationForOnboarding?.trialEndsAt
                ),
              planKey:
                organizationForOnboarding?.planKey ||
                organizationForOnboarding?.planName ||
                null,
            },
            metadata: {
              phase: 'signup_bootstrap',
              templateKey: templateKey || null,
              organizationId: organizationIdForBootstrap,
              stripeCustomerId:
                organizationForOnboarding?.stripeCustomerId || null,
              subscriptionId:
                organizationForOnboarding?.subscriptionId || null,
            },
          });

          if (intent === 'duplicate_template' && templateKey) {
            const entry = TEMPLATE_REGISTRY?.[templateKey] || null;
            const publishedFormId = entry?.publishedFormId || null;
            
            console.log('[SignupLoadingPage] Template registry lookup:', {
              templateKey,
              publishedFormId,
              label: entry?.label || null,
            });

            if (publishedFormId && typeof initializeNewFormLocally === 'function') {
              setStep(100, 'Creating your editable copy…');

              const result = await duplicateForm(
                publishedFormId,
                {
                  currentUser: auth.currentUser,
                  organizationId: organizationIdForBootstrap,
                },
                initializeNewFormLocally
              );

              console.log('[SignupLoadingPage] duplicateForm result:', result);

              if (result?.status === 'success' && result?.newFormId) {
                await recordSignupOnboardingEvent({
                  eventName: ONBOARDING_EVENTS.TEMPLATE_OPENED,
                  eventId:
                    `signup_${uid}_template_opened_${result.newFormId}`,
                  eventProperties: {
                    signupType: SIGNUP_TYPES.SELF_SERVE_TRIAL,
                    signupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
                    signupSource: source || 'unknown',
                    templateKey,
                    duplicatedFormId: result.newFormId,
                  },
                  metadata: {
                    phase: 'template_duplication',
                    templateLabel: entry?.label || null,
                    publishedFormId,
                    organizationId: organizationIdForBootstrap,
                  },
                });

                // Clear intent so it never re-runs on refresh
                sessionStorage.removeItem('signupIntent');
                sessionStorage.removeItem('signupTemplateKey');
                sessionStorage.removeItem('signupSource');

              console.log('[SignupLoadingPage] Redirecting to duplicated form builder:', result.newFormId);
              navigate(`/form-builder/${result.newFormId}/layout`, {
                replace: true,
                state: {
                  templateOnboarding: true,
                  templateKey,
                  templateLabel: entry?.label || 'Your template',
                },
              });
              console.log('--- End heavy-lifting in SignupLoadingPage (template bootstrap) ---');
              return;
              }

              console.warn('[SignupLoadingPage] Template duplication failed; falling back to homepage.');
            } else {
              console.warn('[SignupLoadingPage] Missing publishedFormId or initializeNewFormLocally; falling back.');
            }
          }
        } catch (e) {
          console.warn('[SignupLoadingPage] Template bootstrap failed (non-fatal):', e?.message || e);
        }

        // Default: Redirect to the homepage
        console.log('Redirecting to homepage...');
        navigate('/', { replace: true });
        console.log('--- End heavy-lifting in SignupLoadingPage ---');
      } catch (err) {
        stopTicker();
        console.error('[SignupLoadingPage] Error:', err.message || err);
        setError(`Failed to complete sign-up flow: ${err.message || err}`);

        // Optional: You can sign the user out on error:
        // signOut(auth);

        // If we error, stop blocking unload
        setShowDontClose(false);
      } finally {
        setLoading(false, 'SignupLoading');
      }
    };

    doHeavyWork();
  }, [navigate, setLoading, setAuthState, createOrganization, initializeNewFormLocally]);

  // Render helper for the checklist
  const StepRow = useMemo(() => {
    return ({ label, state }) => {
      const icon =
        state === 'done' ? '✓' :
        state === 'active' ? '⏳' :
        '○';

      const color =
        state === 'done' ? 'text.primary' :
        state === 'active' ? 'text.primary' :
        'text.secondary';

      const weight =
        state === 'active' ? 700 :
        400;

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.75,
          }}
        >
          <Typography variant="body2" sx={{ color, fontWeight: weight }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color, fontWeight: weight }}>
            {icon}
          </Typography>
        </Box>
      );
    };
  }, []);

  //  Solid background, no card
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 560, textAlign: 'center' }}>
        <img
          src={logoSrc}
          alt="UTM Smart Manager"
          style={{ maxWidth: '160px', marginBottom: 12 }}
        />

        {error ? (
          <>
            <Typography variant="h5" color="error" sx={{ mb: 2 }}>
              Error Setting Up Your Account
            </Typography>
            <Typography variant="body1">{error}</Typography>

            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Please refresh the page or contact support if this continues.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box
              display="flex"
              gap={1.5}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Button variant="outlined" onClick={() => navigate('/signup')}>
                Back to Signup
              </Button>
              <Button variant="contained" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Determinate progress with ticker */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <CircularProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, progress))}
                size={72}
                thickness={4}
                color="primary"
              />
            </Box>

            <Typography variant="h6" sx={{ mt: 3 }}>
              {statusText}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1, color: 'text.secondary' }}
            >
              {Math.floor(progress)}% complete
            </Typography>

            {/* “Don’t close this tab” message */}
            {showDontClose && (
              <Typography
                variant="body2"
                sx={{ mt: 2, color: 'text.secondary' }}
              >
                Please keep this tab open — closing it may interrupt setup.
              </Typography>
            )}

            {/* Long-running reassurance */}
            {longWaitMsg && (
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: 'text.secondary' }}
              >
                {longWaitMsg}
              </Typography>
            )}

            {/* Step checklist */}
            <Box
              sx={{
                mt: 3,
                mx: 'auto',
                width: '100%',
                maxWidth: 420,
                textAlign: 'left',
                borderLeft: '4px solid #489DFF',
                pl: 2,
              }}
            >
              <StepRow label="Creating workspace" state={stepState.workspace} />
              <StepRow label="Saving profile" state={stepState.profile} />
              <StepRow label="Confirming setup" state={stepState.sync} />
              <StepRow label="Finalizing sign-in" state={stepState.finalize} />
            </Box>

            {/* Retry (after timeout) */}
            {showRetry && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  If this is taking too long, you can try again.
                </Typography>
                <Box
                  display="flex"
                  gap={1.5}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                  >
                    Retry setup
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/signup')}
                  >
                    Return to Signup
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SignupLoadingPage;
